import { useState, useEffect } from 'react';
import { getJournal, addJournalEntry, deleteJournalEntry, generateAiMemory } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Sparkles, MapPin, BookOpen, Smile, Frown, Meh, Zap, Heart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MOODS = { happy: { icon: Smile, color: '#00CC99', label: 'Content' }, neutral: { icon: Meh, color: '#F59E0B', label: 'Neutre' },
  tired: { icon: Frown, color: '#94A3B8', label: 'Fatigue' }, excited: { icon: Zap, color: '#3A86FF', label: 'Excite' }, sick: { icon: Heart, color: '#F43F5E', label: 'Malade' }};

export const JournalGallery = ({ tripId }) => {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], text: '', mood: 'happy', location: '', tags: '' });
  const [generating, setGenerating] = useState({});

  const load = () => { getJournal(tripId).then(r => setEntries(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [tripId]); // eslint-disable-line

  const handleAdd = async () => {
    if (!form.text) return;
    await addJournalEntry(tripId, form);
    setForm({ date: new Date().toISOString().split('T')[0], text: '', mood: 'happy', location: '', tags: '' });
    setOpen(false); toast.success('Souvenir ajoute !'); load();
  };

  const handleDelete = async (id) => { await deleteJournalEntry(id); load(); };

  const handleAiMemory = async (entryId) => {
    setGenerating(p => ({ ...p, [entryId]: true }));
    try {
      await generateAiMemory(tripId, entryId);
      toast.success('Souvenir IA genere !'); load();
    } catch { toast.error('Erreur generation'); }
    setGenerating(p => ({ ...p, [entryId]: false }));
  };

  return (
    <div className="space-y-4" data-testid="journal-gallery">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF8EB] flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#1E293B]">Carnet de bord</h3>
            <p className="text-xs text-[#94A3B8]">{entries.length} entrees</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9" data-testid="add-journal-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle entree
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau souvenir</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1 h-11" />
                </div>
                <div>
                  <Label className="text-sm">Humeur</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    {Object.entries(MOODS).map(([key, m]) => (
                      <button key={key} onClick={() => setForm({...form, mood: key})} className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${form.mood === key ? 'ring-2 ring-offset-1' : 'hover:bg-[#F1F5F9]'}`} style={{ ringColor: m.color }}>
                        <m.icon className="w-5 h-5" style={{ color: m.color }} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-sm">Recit</Label>
                <Textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} className="mt-1" rows={4} placeholder="Aujourd'hui nous avons..." data-testid="journal-text" />
              </div>
              <div>
                <Label className="text-sm">Lieu</Label>
                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="mt-1 h-11" placeholder="Torre de Belem, Lisbonne" />
              </div>
              <div>
                <Label className="text-sm">Tags</Label>
                <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="mt-1 h-11" placeholder="plage, famille, coucher de soleil" />
              </div>
              <Button onClick={handleAdd} className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white" data-testid="save-journal-btn">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {entries.length === 0 ? (
        <div className="widget-card p-10 text-center">
          <BookOpen className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-sm text-[#94A3B8]">Commencez votre carnet de bord !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => {
            const mood = MOODS[entry.mood] || MOODS.happy;
            const MoodIcon = mood.icon;
            return (
              <div key={entry.entry_id} className="widget-card overflow-hidden group" data-testid={`journal-${entry.entry_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <MoodIcon className="w-6 h-6" style={{ color: mood.color }} />
                      <div>
                        <p className="text-sm font-semibold text-[#1E293B]">{new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        {entry.location && <p className="text-[10px] text-[#94A3B8] flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {entry.location}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleAiMemory(entry.entry_id)} disabled={generating[entry.entry_id]} className="h-7 px-2 text-[10px] text-[#3A86FF]" data-testid={`ai-memory-${entry.entry_id}`}>
                        {generating[entry.entry_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-0.5" /> Souvenir IA</>}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.entry_id)} className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed">{entry.text}</p>
                  {entry.tags && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {entry.tags.split(',').map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-[9px] rounded-md bg-[#F1F5F9] text-[#64748B]">#{tag.trim()}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                {entry.ai_memory && (
                  <div className="px-5 py-3 bg-gradient-to-r from-[#EBF2FF] to-[#F5F3FF] border-t border-[#E2E8F0]">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-[#3A86FF] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-[#475569] italic leading-relaxed">{entry.ai_memory}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
